import React, { useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Reusable, controlled editor for a channel's USER-declared relevance: GICS sectors +
// sub-industries + tickers. All optional. Codes come from the GICS controlled vocabulary
// (fetched once, module-cached) so nothing is free-typed — deterministic, no invented labels.
const GOLD = 'rgb(var(--brand-gold-rgb))';

let _taxPromise = null;
function loadTaxonomy() {
  if (!_taxPromise) _taxPromise = researchosService.getGicsTaxonomy().then((r) => r.data);
  return _taxPromise;
}

function Chip({ label, onRemove, title }) {
  return (
    <span title={title} className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700 dark:bg-gray-800 dark:text-gray-200">
      {label}
      {onRemove && (
        <button onClick={onRemove} className="text-gray-400 hover:text-red-500" aria-label="remove">×</button>
      )}
    </span>
  );
}

export default function RelevanceEditor({ value, onChange }) {
  const [tax, setTax] = useState(null);
  const [subQuery, setSubQuery] = useState('');
  const [tickerInput, setTickerInput] = useState('');
  const [copyList, setCopyList] = useState([]);   // existing channels with relevance, for "copy from"
  const [showCopy, setShowCopy] = useState(false);

  useEffect(() => { loadTaxonomy().then(setTax).catch(() => setTax({ sectors: [], sub_industries: [] })); }, []);
  useEffect(() => { researchosService.listChannelRelevance().then((r) => setCopyList(r.data || [])).catch(() => {}); }, []);

  // Union another channel's relevance into the current selection (copy-from-existing).
  const copyFrom = (ch) => {
    const uniq = (a, b) => Array.from(new Set([...(a || []), ...(b || [])]));
    onChange({
      sector_codes: uniq(value.sector_codes, ch.sector_codes),
      sub_industry_codes: uniq(value.sub_industry_codes, ch.sub_industry_codes),
      tickers: uniq(value.tickers, ch.tickers),
    });
  };

  const sectorCodes = useMemo(() => value.sector_codes || [], [value.sector_codes]);
  const subCodes = useMemo(() => value.sub_industry_codes || [], [value.sub_industry_codes]);
  const tickers = value.tickers || [];

  const subByCode = useMemo(() => {
    const m = {};
    (tax?.sub_industries || []).forEach((s) => { m[s.code] = s; });
    return m;
  }, [tax]);
  const sectorName = (code) => (tax?.sectors || []).find((s) => s.code === code)?.name || code;

  const toggleSector = (code) => {
    const next = sectorCodes.includes(code) ? sectorCodes.filter((c) => c !== code) : [...sectorCodes, code];
    onChange({ ...value, sector_codes: next });
  };
  const addSub = (code) => {
    if (!subCodes.includes(code)) onChange({ ...value, sub_industry_codes: [...subCodes, code] });
    setSubQuery('');
  };
  const removeSub = (code) => onChange({ ...value, sub_industry_codes: subCodes.filter((c) => c !== code) });
  const addTickers = (raw) => {
    const toks = raw.split(/[\s,]+/).map((t) => t.toUpperCase().trim()).filter(Boolean);
    const next = Array.from(new Set([...tickers, ...toks]));
    onChange({ ...value, tickers: next });
    setTickerInput('');
  };
  const removeTicker = (t) => onChange({ ...value, tickers: tickers.filter((x) => x !== t) });

  // Sub-industry suggestions: restricted to chosen sectors (if any), matching the search text.
  const subMatches = useMemo(() => {
    if (!tax) return [];
    const q = subQuery.trim().toLowerCase();
    if (!q && sectorCodes.length === 0) return [];
    return tax.sub_industries
      .filter((s) => (sectorCodes.length === 0 || sectorCodes.includes(s.sector_code))
        && !subCodes.includes(s.code)
        && (!q || s.name.toLowerCase().includes(q) || s.code.startsWith(q)))
      .slice(0, 8);
  }, [tax, subQuery, sectorCodes, subCodes]);

  if (!tax) return <div className="text-xs text-gray-400">Loading GICS…</div>;

  return (
    <div className="space-y-3">
      {/* Copy from an existing channel — unions its sectors/sub-industries/tickers into this one.
          Click several to combine (e.g. a new "SemiAnalysis Weekly" copying "SemiAnalysis"). */}
      {copyList.length > 0 && (
        <div>
          <button onClick={() => setShowCopy((v) => !v)}
            className="text-[11px] uppercase tracking-wider text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            {showCopy ? '▾' : '▸'} Copy from existing channel
          </button>
          {showCopy && (
            <div className="mt-1 flex flex-wrap gap-1">
              {copyList.map((ch) => (
                <button key={ch.channel_id} onClick={() => copyFrom(ch)}
                  title={`${(ch.sector_codes || []).length} sectors · ${(ch.sub_industry_codes || []).length} sub-industries · ${(ch.tickers || []).length} tickers`}
                  className="rounded border border-gray-300 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/40">
                  + {ch.display_name || ch.channel_id}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Sectors */}
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-wider text-gray-500">Sectors</div>
        <div className="flex flex-wrap gap-1">
          {tax.sectors.map((s) => {
            const on = sectorCodes.includes(s.code);
            return (
              <button key={s.code} onClick={() => toggleSector(s.code)}
                className="rounded px-2 py-0.5 text-[11px]"
                style={on ? { color: GOLD, border: `1px solid ${GOLD}` } : { border: '1px solid transparent', color: '#6b7280' }}>
                {s.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sub-industries */}
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-wider text-gray-500">Sub-industries</div>
        {subCodes.length > 0 && (
          <div className="mb-1 flex flex-wrap gap-1">
            {subCodes.map((c) => (
              <Chip key={c} label={subByCode[c]?.name || c} title={`${c} · ${sectorName(c.slice(0, 2))}`} onRemove={() => removeSub(c)} />
            ))}
          </div>
        )}
        <input value={subQuery} onChange={(e) => setSubQuery(e.target.value)}
          placeholder={sectorCodes.length ? 'Search sub-industries in chosen sectors…' : 'Pick a sector first, or search all…'}
          className="block w-full rounded border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700" />
        {subMatches.length > 0 && (
          <div className="mt-1 rounded border border-gray-200 dark:border-gray-800">
            {subMatches.map((s) => (
              <button key={s.code} onClick={() => addSub(s.code)}
                className="block w-full px-2 py-1 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-800/40">
                {s.name} <span className="text-gray-400">· {sectorName(s.sector_code)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tickers */}
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-wider text-gray-500">Tickers</div>
        {tickers.length > 0 && (
          <div className="mb-1 flex flex-wrap gap-1">
            {tickers.map((t) => <Chip key={t} label={t} onRemove={() => removeTicker(t)} />)}
          </div>
        )}
        <input value={tickerInput}
          onChange={(e) => setTickerInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTickers(tickerInput); } }}
          onBlur={() => tickerInput.trim() && addTickers(tickerInput)}
          placeholder="NVDA, AMD, TSM — Enter to add"
          className="block w-full rounded border border-gray-300 bg-transparent px-2 py-1 text-xs uppercase dark:border-gray-700" />
      </div>
    </div>
  );
}
